import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAccountApi } from '../../../common/hooks/use-account-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { isStrongPassword } from '../../../common/utils/password-policy';
import { AccountHeading, AccountNotice, AccountPage, PasswordField } from './components/AccountPage';

type Phase = { kind: 'checking' } | { kind: 'invalid' } | { kind: 'busy' } | { kind: 'form'; email: string } | { kind: 'done' };

/*
 * A reset signs this browser out too, and signing out remounts the whole app
 * (ThemeWrapper keys it on the auth state). Remember, for this tab, the link
 * that was just used, so the remounted page says "Password changed" instead
 * of re-checking a link it spent a moment ago.
 */
const DONE_KEY = 'ardealy.reset-password.done';
const rememberDone = (token: string) => {
    try {
        sessionStorage.setItem(DONE_KEY, token);
    } catch {
        // private mode: the page may then say the link was used, which is also true
    }
};
const wasDone = (token: string) => {
    try {
        return sessionStorage.getItem(DONE_KEY) === token;
    } catch {
        return false;
    }
};

/** Where a reset link lands (/reset-password/:token). The link is checked before asking for anything. */
const ResetPasswordPage = () => {
    const { t } = useTranslation();
    const { token = '' } = useParams();
    const navigate = useNavigate();
    const accountApi = useAccountApi();

    const [phase, setPhase] = useState<Phase>(() => (wasDone(token) ? { kind: 'done' } : { kind: 'checking' }));
    const [password, setPassword] = useState('');
    const [check, setCheck] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (wasDone(token)) return;
        let live = true;
        accountApi
            .checkPasswordReset(token)
            .then(({ email }) => live && setPhase({ kind: 'form', email }))
            .catch(async (e) => {
                const { status } = await readApiError(e);
                if (live) setPhase(status === 429 ? { kind: 'busy' } : { kind: 'invalid' });
            });
        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const passwordOk = isStrongPassword(password);

    const submit = async () => {
        setCheck(true);
        setError(null);
        if (!passwordOk || saving) return;
        setSaving(true);
        try {
            await accountApi.resetPassword(token, password);
            rememberDone(token);
            setPhase({ kind: 'done' });
        } catch (e) {
            const { status, code } = await readApiError(e);
            if (code === 'TOKEN_INVALID') setPhase({ kind: 'invalid' });
            else setError(status === 429 ? t('ACCOUNT.COMMON.TOO_MANY') : t('ACCOUNT.COMMON.GENERIC_ERROR'));
        } finally {
            setSaving(false);
        }
    };

    if (phase.kind === 'checking') {
        return (
            <AccountPage>
                <p className="mb-4 text-center text-sm text-muted-foreground" role="status">{t('ACCOUNT.RESET.CHECKING')}</p>
                <Skeleton className="h-40 rounded-xl" />
            </AccountPage>
        );
    }

    if (phase.kind === 'busy') {
        return (
            <AccountPage>
                <AccountNotice tone="error" testId="reset-busy">{t('ACCOUNT.COMMON.TOO_MANY')}</AccountNotice>
                <Button variant="outline" className="h-11 w-full" onClick={() => navigate('/login')}>
                    {t('ACCOUNT.COMMON.BACK_TO_LOGIN')}
                </Button>
            </AccountPage>
        );
    }

    if (phase.kind === 'invalid') {
        return (
            <AccountPage>
                <AccountNotice tone="error" title={t('ACCOUNT.RESET.INVALID_TITLE')} testId="reset-invalid">
                    {t('ACCOUNT.RESET.INVALID')}
                </AccountNotice>
                <Button className="h-11 w-full" onClick={() => navigate('/forgot-password')}>
                    {t('ACCOUNT.RESET.REQUEST_NEW')}
                </Button>
            </AccountPage>
        );
    }

    if (phase.kind === 'done') {
        return (
            <AccountPage>
                <AccountNotice tone="success" title={t('ACCOUNT.RESET.DONE_TITLE')} testId="reset-done">
                    {t('ACCOUNT.RESET.DONE')}
                </AccountNotice>
                <Button className="h-11 w-full" onClick={() => navigate('/login')}>
                    {t('ACCOUNT.INVITE.GO_TO_LOGIN')}
                </Button>
            </AccountPage>
        );
    }

    return (
        <AccountPage>
            <AccountHeading title={t('ACCOUNT.RESET.TITLE')} subtitle={t('ACCOUNT.RESET.SUBTITLE', { email: phase.email })} />
            {error && <AccountNotice tone="error">{error}</AccountNotice>}
            <PasswordField
                label={`${t('ACCOUNT.COMMON.NEW_PASSWORD_LABEL')}*`}
                value={password}
                onChange={setPassword}
                onKeyDown={(e: any) => e.key === 'Enter' && submit()}
                error={check && !passwordOk}
                errorMessage={t('ACCOUNT.COMMON.PASSWORD_WEAK')}
                showRules
            />
            <Button className="mt-6 h-11 w-full" onClick={submit} disabled={saving}>
                {t('ACCOUNT.RESET.SUBMIT')}
            </Button>
        </AccountPage>
    );
};

export default ResetPasswordPage;
